/**
 * useSchedulerConnection
 *
 * Shared hook that reads/writes the scheduler connection state from localStorage.
 * This ensures that connecting/disconnecting in Settings → Integrations is
 * immediately reflected everywhere in the app (Resource Budget, Scheduling tab,
 * Dashboard alerts, etc.) without requiring a backend round-trip during development.
 *
 * When the real API is wired up, replace the localStorage reads/writes with
 * API calls and this hook becomes a thin wrapper around React Query.
 */

import { useState, useEffect, useCallback } from 'react';
import { mockSchedulerConnection } from '@/lib/mock-scheduling-data';
import type { SchedulerConnection, SchedulerConnectionStatus, SchedulerType } from '@/lib/mock-scheduling-data';

const STORAGE_KEY = 'evo_scheduler_connection';

function readFromStorage(): SchedulerConnection {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw) as SchedulerConnection;
  } catch {
    // ignore parse errors
  }
  // Fall back to mock data seed value
  return mockSchedulerConnection;
}

// Custom event name for same-tab broadcasts (StorageEvent only fires in other tabs)
const STORAGE_CHANGE_EVENT = 'evo_scheduler_connection_changed';

function writeToStorage(connection: SchedulerConnection): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(connection));
    // Broadcast to same-tab listeners (StorageEvent doesn't fire in the same tab)
    window.dispatchEvent(new CustomEvent(STORAGE_CHANGE_EVENT));
  } catch {
    // ignore write errors (e.g. private browsing quota)
  }
}

// ---------------------------------------------------------------------------
// Public hook
// ---------------------------------------------------------------------------

export interface UseSchedulerConnectionReturn {
  connection: SchedulerConnection;
  isConnected: boolean;
  setConnection: (connection: SchedulerConnection) => void;
}

export function useSchedulerConnection(): UseSchedulerConnectionReturn {
  const [connection, setConnectionState] = useState<SchedulerConnection>(readFromStorage);

  // Keep in sync when another component or tab writes to storage
  useEffect(() => {
    // Same-tab updates (custom event fired by writeToStorage)
    const handleCustom = () => setConnectionState(readFromStorage());
    // Cross-tab updates (native StorageEvent)
    const handleStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY || e.key === null) {
        setConnectionState(readFromStorage());
      }
    };
    window.addEventListener(STORAGE_CHANGE_EVENT, handleCustom);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener(STORAGE_CHANGE_EVENT, handleCustom);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const setConnection = useCallback((next: SchedulerConnection) => {
    writeToStorage(next);
    setConnectionState(next);
  }, []);

  return {
    connection,
    isConnected: connection.status === 'connected',
    setConnection,
  };
}

// ---------------------------------------------------------------------------
// Non-hook helper for components that can't use hooks (e.g. utility functions)
// ---------------------------------------------------------------------------

export function getIsSchedulerConnected(): boolean {
  return readFromStorage().status === 'connected';
}
